// src/components/VIPGuests.tsx
import React, { useCallback, useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import styled, { keyframes } from "styled-components"
import Hero from "./Hero"
import { fetchGuidelineAssets, GuidelineAsset } from "../../../utils/fetchGuidelineAssets"

type MediaItem = { asset: GuidelineAsset; kind: "video" | "photo" | "url" }

// ----- Styled Components -----
const PageWrapper = styled.div`
  background: #f9fafb;
  min-height: 100vh;
`

const GuidelinesContainer = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

const Sidebar = styled.aside`
  position: sticky;
  top: 80px;
  align-self: flex-start;
  background: #ffffff;
  padding: 1.5rem 1rem;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  max-height: calc(100vh - 80px);
  overflow-y: auto;

  h1 {
    margin: 0 0 1rem;
    font-size: 1.25rem;
  }

  @media (max-width: 900px) {
    position: relative;
    top: 0;
    max-height: none;
  }
`

const Pill = styled.button`
  display: block;
  width: 100%;
  background: #f3f4f6;
  border: none;
  border-radius: 12px;
  padding: 0.65rem 1rem;
  font-size: 0.95rem;
  margin-bottom: 0.45rem;
  text-align: left;
  cursor: pointer;
  transition:
    background 0.2s,
    transform 0.2s;

  &.active {
    background: linear-gradient(135deg, #2563eb, #1d4ed8);
    color: white;
    box-shadow: 0 10px 25px rgba(37, 99, 235, 0.25);
  }

  &:hover {
    background: #e5e7eb;
    transform: translateX(4px);
  }
`

const MainContent = styled.main`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`

const Section = styled.section`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.05);
`

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;

  h2 {
    font-size: 1.5rem;
    margin: 0;
  }
`

const SectionBadge = styled.span`
  background: #e0f2fe;
  color: #0369a1;
  border-radius: 999px;
  padding: 0.35rem 0.75rem;
  font-size: 0.85rem;
  font-weight: 600;
`

const MediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1rem;
  margin-top: 1.25rem;
`

const MediaCard = styled.div`
  background: #f9fafb;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  min-height: 260px;

  .media-top {
    position: relative;
    padding-bottom: 60%;
    background: #0b1223;
    overflow: hidden;

    iframe,
    img {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border: none;
      object-fit: cover;
      background: #0b1223;
    }

    /* We never mount <video> in the grid, so there is ZERO video preload here */
  }

  .media-meta {
    padding: 1rem;
    display: grid;
    gap: 0.35rem;

    h3 {
      margin: 0;
      font-size: 1rem;
    }

    p {
      margin: 0;
      color: #4b5563;
      font-size: 0.9rem;
      line-height: 1.4;
    }
  }
`

const VideoPlaceholder = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 30% 10%, #1e293b, #020617);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #e5e7eb;
  font-size: 3rem;
  transition:
    background 0.2s,
    transform 0.2s;

  &:hover {
    background: radial-gradient(circle at 30% 10%, #111827, #000000);
    transform: scale(1.02);
  }
`

const MediaModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;

  .modal-content {
    position: relative;
    width: 90%;
    max-width: 1200px;
    background: #000;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    background: #1a1a1a;
    border-bottom: 1px solid #333;

    h3 {
      margin: 0;
      color: white;
      font-size: 1.25rem;
    }

    .close-button {
      background: transparent;
      border: none;
      color: white;
      font-size: 2rem;
      cursor: pointer;
      padding: 0;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      transition: background 0.2s;

      &:hover {
        background: rgba(255, 255, 255, 0.1);
      }
    }
  }

  .modal-video-container {
    position: relative;
    width: 100%;
    padding-bottom: 56.25%;
    height: 0;
    overflow: hidden;

    video,
    iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border: none;
      background: #000;
      object-fit: contain;
    }

    img {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }
`

const MediaTypeTag = styled.span`
  align-self: flex-start;
  background: #eef2ff;
  color: #312e81;
  border-radius: 8px;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
  gap: 0.75rem;
  color: #0f172a;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #e5e7eb;
    border-top-color: #2563eb;
    border-radius: 50%;
    animation: ${spin} 1s linear infinite;
  }

  .label {
    font-weight: 700;
    letter-spacing: 0.01em;
  }
`

const InitialLoadingShell = styled(LoadingState)`
  min-height: 50vh;
  background: #f9fafb;
  border-radius: 12px;
`

// Map folder names to category keys
const categoryMapping: Record<string, string> = {
  "beach-and-bbq": "beach",
  "beach-and-bbq-and-exercise": "beach",
  "beach-and-bbq-and-exerci-e": "beach",
  "closet-photos": "closet",
  "vip-closet-photos": "closet",
  "food-and-kitchen": "food",
  "medical-and-beauty": "medical",
  "reseal-vacuum-bags-before-you-leave-please": "vacuum",
  "reseal-vacuum-bags": "vacuum",
  "technology-items": "technology",
}

const assetLabelOverrides: Record<string, string> = {
  "beach-and-bbq-and-exercise": "Beach And BBQ And Exercise",
  "beach-and-bbq-and-exerci-e": "Beach And BBQ And Exercise",
}

const formatLabel = (value: string): string =>
  value
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase())

const getVideoMimeType = (url: string) => {
  const lower = url.toLowerCase()
  if (lower.endsWith(".mp4")) return "video/mp4"
  if (lower.endsWith(".webm")) return "video/webm"
  if (lower.endsWith(".ogg")) return "video/ogg"
  if (lower.endsWith(".mov")) return "video/quicktime"
  if (lower.endsWith(".avi")) return "video/x-msvideo"
  if (lower.endsWith(".mkv")) return "video/x-matroska"
  return undefined
}

// ----- VIPGuests Component -----
const VIPGuests: React.FC = () => {
  const { t, i18n } = useTranslation("vipGuests")
  const [activeCat, setActiveCat] = useState<string>("")
  const [assets, setAssets] = useState<GuidelineAsset[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [poppedOutVideo, setPoppedOutVideo] = useState<{ video: GuidelineAsset; category: string } | null>(null)
  const [poppedOutPhoto, setPoppedOutPhoto] = useState<{ photo: GuidelineAsset; category: string } | null>(null)
  const [categories, setCategories] = useState<{ id: string; key: string }[]>([])

  useEffect(() => {
    const loadAssets = async () => {
      try {
        setLoading(true)
        const bucketPath =
          import.meta.env.VITE_VIP_GUESTS_ASSETS_BASE_PATH || "VIP guests info/VIP Access to Owners CLOSET"
        console.log(`[VIPGuests] Loading assets from path: ${bucketPath}`)
        const fetchedAssets = await fetchGuidelineAssets(bucketPath)
        console.log(`[VIPGuests] Fetched ${fetchedAssets.length} assets:`, fetchedAssets)

        const normalizedAssets = fetchedAssets.map((asset) => {
          const slugCat = asset.cat.toLowerCase().replace(/[\\s/]+/g, "-")
          const normalizedCat = categoryMapping[slugCat] || slugCat
          return { ...asset, cat: normalizedCat }
        })

        setAssets(normalizedAssets)

        const discoveredCategories = Array.from(new Set(normalizedAssets.map((asset) => asset.cat))).map((cat) => ({
          id: cat,
          key: cat,
        }))

        setCategories(discoveredCategories)
        if (discoveredCategories.length > 0) {
          setActiveCat((prev) => prev || discoveredCategories[0].id)
        }
      } catch (error) {
        console.error("[VIPGuests] Error loading guideline assets:", error)
      } finally {
        setLoading(false)
      }
    }

    void loadAssets()
  }, [])

  const initialLoading = loading && categories.length === 0

  const scrollTo = useCallback((id: string) => {
    const el = document.getElementById(id)
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY
      window.scrollTo({ top: Math.max(top - 80, 0), behavior: "smooth" })
      setActiveCat(id)
    }
  }, [])

  const getVideosForCategory = (categoryKey: string): GuidelineAsset[] =>
    assets.filter((asset) => asset.type === "video" && asset.cat === categoryKey)

  const getPhotosForCategory = (categoryKey: string): GuidelineAsset[] =>
    assets.filter((asset) => asset.type === "photo" && asset.cat === categoryKey)

  const getCategoryLabel = (catKey: string) => {
    const translationKey = `categories.${catKey}`
    return i18n.exists(translationKey) ? t(translationKey) : formatLabel(catKey)
  }

  const getVideoTitle = (catKey: string, assetId: string) => {
    const translationKey = `videos.${catKey}.${assetId}.title`
    if (assetLabelOverrides[assetId]) return assetLabelOverrides[assetId]
    return i18n.exists(translationKey) ? t(translationKey) : formatLabel(assetId)
  }

  const getVideoDescription = (catKey: string, assetId: string) => {
    const translationKey = `videos.${catKey}.${assetId}.description`
    return i18n.exists(translationKey) ? t(translationKey) : ""
  }

  const getPhotoCaption = (catKey: string, assetId: string) => {
    const translationKey = `photos.${catKey}.${assetId}`
    if (assetLabelOverrides[assetId]) return assetLabelOverrides[assetId]
    return i18n.exists(translationKey) ? t(translationKey) : formatLabel(assetId)
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (poppedOutVideo) setPoppedOutVideo(null)
        if (poppedOutPhoto) setPoppedOutPhoto(null)
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [poppedOutVideo, poppedOutPhoto])

  const getMediaItemsForCategory = (categoryKey: string): MediaItem[] => {
    const videos: MediaItem[] = getVideosForCategory(categoryKey).map((asset) => ({
      asset,
      kind: "video",
    }))

    const urls: MediaItem[] = assets
      .filter((asset) => asset.type === "url" && asset.cat === categoryKey)
      .map((asset) => ({ asset, kind: "url" as const }))

    const photos: MediaItem[] = getPhotosForCategory(categoryKey).map((asset) => ({
      asset,
      kind: "photo",
    }))

    return [...videos, ...urls, ...photos]
  }

  return (
    <PageWrapper>
      <Hero />
      <GuidelinesContainer>
        {initialLoading ? (
          <InitialLoadingShell>
            <div className="spinner" aria-hidden />
            <div className="label">Loading photos & videos…</div>
          </InitialLoadingShell>
        ) : (
          <>
            <Sidebar>
              <h1>{t("title")}</h1>
              {categories.map((cat) => (
                <Pill key={cat.id} onClick={() => scrollTo(cat.id)} className={activeCat === cat.id ? "active" : ""}>
                  {getCategoryLabel(cat.key)}
                </Pill>
              ))}
            </Sidebar>

            <MainContent>
              {categories.map((cat) => {
                const mediaItems = getMediaItemsForCategory(cat.key)
                return (
                  <Section id={cat.id} key={cat.id}>
                    <SectionTitle>
                      <h2>{getCategoryLabel(cat.key)}</h2>
                      <SectionBadge>
                        {mediaItems.length > 0 ? `${mediaItems.length} ${mediaItems.length === 1 ? "item" : "items"}` : t("noMedia", "No media yet")}
                      </SectionBadge>
                    </SectionTitle>

                    {loading ? (
                      <LoadingState>
                        <div className="spinner" aria-hidden />
                        <div className="label">Loading photos & videos…</div>
                      </LoadingState>
                    ) : mediaItems.length === 0 ? (
                      <div>No media available for this category.</div>
                    ) : (
                      <MediaGrid>
                        {mediaItems.map(({ asset, kind }) => {
                          const isVideo = kind === "video" || kind === "url"
                          const title = isVideo ? getVideoTitle(cat.key, asset.id) : getPhotoCaption(cat.key, asset.id)
                          const description = isVideo ? getVideoDescription(cat.key, asset.id) : ""

                          const useIframe =
                            isVideo &&
                            asset.url.startsWith("http") &&
                            !asset.url.includes(".mp4") &&
                            !asset.url.includes(".webm") &&
                            !asset.url.includes(".mov") &&
                            !asset.url.includes(".avi") &&
                            !asset.url.includes(".mkv")

                          return (
                            <MediaCard key={`${asset.id}-${kind}`}>
                              <div className="media-top">
                                {isVideo ? (
                                  <>
                                    {/* GRID: NO <video> HERE → NO PRELOAD / DOWNLOAD */}
                                    <VideoPlaceholder
                                      role="button"
                                      tabIndex={0}
                                      onClick={() => setPoppedOutVideo({ video: asset, category: cat.key })}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                          e.preventDefault()
                                          setPoppedOutVideo({ video: asset, category: cat.key })
                                        }
                                      }}
                                      aria-label={`Play ${title}`}
                                    >
                                      ▶
                                    </VideoPlaceholder>
                                  </>
                                ) : (
                                  <img
                                    src={asset.url}
                                    alt={title}
                                    loading="lazy"
                                    onClick={() => setPoppedOutPhoto({ photo: asset, category: cat.key })}
                                    style={{ cursor: "zoom-in" }}
                                  />
                                )}
                              </div>

                              <div className="media-meta">
                                <MediaTypeTag>{isVideo ? "Video" : "Photo"}</MediaTypeTag>
                                <h3>{title}</h3>
                                {description && <p>{description}</p>}
                              </div>
                            </MediaCard>
                          )
                        })}
                      </MediaGrid>
                    )}
                  </Section>
                )
              })}
            </MainContent>
          </>
        )}

        {/* Video Pop-out Modal */}
        {poppedOutVideo && (
          <MediaModal
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setPoppedOutVideo(null)
              }
            }}
          >
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{getVideoTitle(poppedOutVideo.category, poppedOutVideo.video.id)}</h3>
                <button className="close-button" onClick={() => setPoppedOutVideo(null)} aria-label="Close video">
                  ×
                </button>
              </div>
              <div className="modal-video-container">
                {(() => {
                  const video = poppedOutVideo.video
                  const useIframe =
                    video.url.startsWith("http") &&
                    !video.url.includes(".mp4") &&
                    !video.url.includes(".webm") &&
                    !video.url.includes(".mov") &&
                    !video.url.includes(".avi") &&
                    !video.url.includes(".mkv")

                  if (useIframe) {
                    // e.g. YouTube or other embed provider
                    return (
                      <iframe
                        src={`${video.url}${video.url.includes("?") ? "&" : "?"}rel=0`}
                        title={getVideoTitle(poppedOutVideo.category, poppedOutVideo.video.id)}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                        allowFullScreen
                      />
                    )
                  }

                  return (
                    <video
                      controls
                      controlsList="nodownload"
                      preload="metadata" // browser only fetches metadata until user plays
                      playsInline
                      autoPlay={false} // explicit
                      title={getVideoTitle(poppedOutVideo.category, poppedOutVideo.video.id)}
                      onLoadedMetadata={(e) => {
                        try {
                          // ensure it's paused at start; no auto play
                          e.currentTarget.pause()
                          e.currentTarget.currentTime = 0
                        } catch {
                          // ignore
                        }
                      }}
                    >
                      <source src={video.url} type={getVideoMimeType(video.url) || "video/mp4"} />
                    </video>
                  )
                })()}
              </div>
            </div>
          </MediaModal>
        )}

        {/* Photo Pop-out Modal */}
        {poppedOutPhoto && (
          <MediaModal
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setPoppedOutPhoto(null)
              }
            }}
          >
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{getPhotoCaption(poppedOutPhoto.category, poppedOutPhoto.photo.id)}</h3>
                <button className="close-button" onClick={() => setPoppedOutPhoto(null)} aria-label="Close photo">
                  ×
                </button>
              </div>
              <div className="modal-video-container" style={{ background: "#000" }}>
                <img
                  src={poppedOutPhoto.photo.url}
                  alt={getPhotoCaption(poppedOutPhoto.category, poppedOutPhoto.photo.id)}
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              </div>
            </div>
          </MediaModal>
        )}
      </GuidelinesContainer>
    </PageWrapper>
  )
}

export default VIPGuests
